import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command';
import {
	FormControl,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, LoaderCircle } from 'lucide-react';

interface ComboboxSelectProps<T = string> {
	field: {
		value?: string | number;
		onChange: (value: string | number) => void;
	};
	options?: T[];
	valueKey?: keyof T;
	displayKey?: keyof T;
	isLoading?: boolean;
	label?: string;
	placeholder?: string;
	searchPlaceholder?: string;
	required?: boolean;
	disabled?: boolean;
	includeOtherOption?: boolean;
	otherOptionLabel?: string;
	onOtherSelected?: (isOther: boolean) => void;
	isOtherSelected?: boolean;
	className?: string;
	emptyMessage?: string;
	loadingMessage?: string;
	skipValidationWhenOther?: boolean;
}

export function ComboboxSelect<T = string>({
	field,
	options = [],
	isLoading = false,
	label = 'Select Option',
	placeholder = 'Select an option...',
	searchPlaceholder = 'Search options...',
	required = false,
	disabled = false,
	includeOtherOption = false,
	otherOptionLabel = 'Other',
	onOtherSelected,
	isOtherSelected = false,
	skipValidationWhenOther = true,
	className,
	emptyMessage = 'No options found.',
	loadingMessage = 'Loading options...',
	valueKey,
	displayKey,
}: ComboboxSelectProps<T>) {
	const [open, setOpen] = useState(false);
	const getOptionValue = (option: T): string | number => {
		if (typeof option === 'string') return option;
		if (valueKey) return option[valueKey] as string | number;
		return String(option);
	};

	const getOptionDisplay = (option: T): string => {
		if (typeof option === 'string') return option;
		if (displayKey) return String(option[displayKey]);
		return String(option);
	};

	const allOptions = includeOtherOption
		? [...options, otherOptionLabel as T]
		: options;

	const handleSelect = (currentValue: string) => {
		if (includeOtherOption && onOtherSelected) {
			const isOther =
				currentValue.toLowerCase() === otherOptionLabel.toLowerCase();
			onOtherSelected(isOther);

			if (isOther) {
				field.onChange('');
				setOpen(false);
				return;
			}
		}

		// Find the matching option
		const selectedOption = options.find(
			(option) =>
				getOptionDisplay(option).toLowerCase() === currentValue.toLowerCase(),
		);

		if (selectedOption) {
			const newValue = getOptionValue(selectedOption);
			field.onChange(newValue === field.value ? '' : newValue);
		} else {
			field.onChange(currentValue === field.value ? '' : currentValue);
		}

		setOpen(false);
	};

	const getDisplayValue = () => {
		if (isLoading) {
			return (
				<div className="flex items-center">
					<LoaderCircle size={16} className="mr-2 animate-spin" />
					{loadingMessage}
				</div>
			);
		}

		if (isOtherSelected && includeOtherOption) {
			return otherOptionLabel;
		}

		if (field.value) {
			const selectedOption = options.find(
				(option) => getOptionValue(option) === field.value,
			);
			return selectedOption ? getOptionDisplay(selectedOption) : placeholder;
		}

		return placeholder;
	};

	const isItemSelected = (option: T) => {
		if (
			includeOtherOption &&
			typeof option === 'string' &&
			option === otherOptionLabel
		) {
			return isOtherSelected;
		}
		return field.value === getOptionValue(option);
	};

	return (
		<FormItem className={cn('flex flex-col', className)}>
			<FormLabel>
				{label} {required && <span className="text-red-500">*</span>}
			</FormLabel>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<FormControl>
						<Button
							variant="outline"
							role="combobox"
							className={cn(
								'w-full justify-between font-normal',
								!field.value && 'text-muted-foreground',
							)}
							disabled={isLoading || disabled}
						>
							{getDisplayValue()}
							<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
						</Button>
					</FormControl>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0">
					<Command>
						<CommandInput placeholder={searchPlaceholder} disabled={isLoading} />
						<CommandList
							onWheelCapture={(e) => {
								e.stopPropagation();
								const target = e.currentTarget;
								target.scrollTop += e.deltaY;
							}}
						>
							{isLoading ? (
								<CommandEmpty>
									<div className="flex items-center justify-center p-2">
										<LoaderCircle size={16} className="mr-2 animate-spin" />
										Loading...
									</div>
								</CommandEmpty>
							) : (
								<>
									<CommandEmpty>{emptyMessage}</CommandEmpty>
									<CommandGroup>
										{allOptions?.map((option, index) => {
											const displayValue = getOptionDisplay(option);
											const optionValue = getOptionValue(option);
											return (
												<CommandItem
													key={`${optionValue}-${index}`}
													value={displayValue}
													onSelect={handleSelect}
												>
													<Check
														className={cn(
															'mr-2 h-4 w-4',
															isItemSelected(option) ? 'opacity-100' : 'opacity-0',
														)}
													/>
													{displayValue}
												</CommandItem>
											);
										})}
									</CommandGroup>
								</>
							)}
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
			{!(skipValidationWhenOther && isOtherSelected) && <FormMessage />}
		</FormItem>
	);
}
